import React, { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import useLanguage from "../../utils/useLanguage";
import nextIcon from "../../assets/next_icon.png";
import padlockIcon from "../../assets/padlock_icon.png";
import previousIcon from "../../assets/previous_icon.png";
import saveIcon from "../../assets/save_icon.png";
import CustomButton from "../../components/CustomButton";
import CustomCheckbox from "../../components/CustomCheckbox";
import CustomInput from "../../components/CustomInput";
import Dropdown from "../../components/Dropdown";
import { Link } from "react-router-dom";
import { toMoney } from "../../utils/toMoney";
import Modal from "../../components/Modal";
import closeIcon from "../../assets/close_icon.png";
import Loader, { LOADER_SIZES } from "../../components/Loader";
import InputLabel from "../../components/InputLabel";
import PasswordInput from "../../components/PasswordInput";
import ErrorBox from "../../components/ErrorBox";
import { UserAuth } from "../../utils/AuthContext";
import axiosClient from "../../services/axiosClient";
import PortalDropdown from "../../components/PortalDropdown";
import AreYouSureModal from "../../components/AreYouSureModal";

export const TABLE_CELL_TYPES = Object.freeze({
  TEXT: "text",
  ELEMENT: "element",
  MONEY: "money",
  TIME: "time",
});

function calculateHours(timeObj = { start: "", end: "" }) {
  const today = new Date().toDateString();
  const startTime = new Date(`${today} ${timeObj.start}`);
  const endTime = new Date(`${today} ${timeObj.end}`);

  const diffMs = endTime - startTime;
  const diffHours = diffMs / (1000 * 60 * 60);

  return diffHours;
}

function formatHours(hours) {
  const wholeHours = Math.floor(hours);
  const minutes = Math.round((hours - wholeHours) * 60);
  return `${wholeHours}:${minutes}`;
}

const EmployeeAttendanceTable = ({
  titles,
  employees,
  setEmployees,
  currentDate,
  formatDate,
  onSaveAttendance,
  onLogChange,
  handleStatusChange,
  calculateDailySalary,
  pagination,
  setPagination,
  withCheckbox = false,
  changedEmployees,
}) => {
  const userType = localStorage.getItem("userType");
  const { userData } = useContext(UserAuth);
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [unlockLoading, setUnlockLoading] = useState(false);
  const [unlockSalaryError, setUnlockSalaryError] = useState(null);
  const [password, setPassword] = useState("");
  const [email, setEmail] = useState(userData.email);
  const [showAreYouSureModal, setShowAreYouSureModal] = useState(false);
  const [paginationType, setPaginationType] = useState("next");

  // HOOKS
  const { t } = useTranslation("general");
  const { currentDirection } = useLanguage();

  const renderTableCell = (row) => {
    return row.map((cell, index, array) => {
      const value = cell.value || "-";
      const sign = cell?.sign || "";
      const bold = cell?.bold ? "t-bold_bold" : "";
      const firstCell =
        !withCheckbox && index === 0 ? "the-rounded-first-cell" : "";
      const lastCell =
        index === array.length - 1 ? "the-rounded-last-cell" : "";
      const roundedCorners = `${firstCell} ${lastCell}`;
      const noWrapText = cell.noWrapText ? "" : "t-eclipse-text";
      const keepOnMobile =
        index > 0 ? (cell.keepOnMobile ? "" : "mobile-hider") : "";

      switch (cell.type) {
        case TABLE_CELL_TYPES.TEXT:
          return (
            <td
              className={`cell ${bold} ${sign} ${roundedCorners} ${keepOnMobile}`}
              key={index}
            >
              <p className={noWrapText}>{value}</p>
            </td>
          );

        case TABLE_CELL_TYPES.LINK:
          return (
            <td
              className={`cell ${bold} ${roundedCorners} ${keepOnMobile}`}
              key={index}
            >
              <Link to={cell?.to} className={`link ${noWrapText}`}>
                {value}
              </Link>
            </td>
          );
        case TABLE_CELL_TYPES.MONEY:
          return (
            <td
              className={`cell ${bold} ${sign} ${roundedCorners} ${keepOnMobile}`}
              key={index}
            >
              {value}
            </td>
          );
        case TABLE_CELL_TYPES.TIME:
          return (
            <td
              className={`cell ${roundedCorners} ${keepOnMobile}`}
              key={index}
            >
              {value}
            </td>
          );
        case TABLE_CELL_TYPES.ELEMENT:
          return (
            <td
              className={`cell ${roundedCorners} ${keepOnMobile}`}
              key={index}
            >
              {value}
            </td>
          );
        default:
          return (
            <td
              className={`cell ${bold} ${sign} ${roundedCorners} ${keepOnMobile}`}
              key={index}
            >
              <div className={noWrapText}>{value}</div>
            </td>
          );
      }
    });
  };

  const buildEmployeeRows = () => {
    const userType = localStorage.getItem("userType");
    const userRole = localStorage.getItem("role");
    const statusOptions = [
      { value: "PRESENT", title: t("present") },
      { value: "OFFDAY", title: t("offday") },
      { value: "ABSENT", title: t("absent") },
      { value: "VACATION", title: t("vacation") },
    ];

    const isOffDay = () => {
      const date = new Date(currentDate);
      const day = date.getDay();
      return day === 5 || day === 6;
    };

    return employees.map((emp) => {
      const dateKey = formatDate(currentDate);
      const totalHours = formatHours(calculateHours(emp.logs[dateKey]?.at(0)));

      const attendanceDay = new Date(dateKey);
      const currentDay = new Date();
      const attendanceDayISO = attendanceDay.toISOString().split("T")[0];
      const currentDayISO = currentDay.toISOString().split("T")[0];
      const isSameUser = emp.id === Number(userData?.userId);

      const canEditAttendance =
        userType === "organization" ||
        userRole === "userAdmin" ||
        (new Date(attendanceDayISO).getTime() >=
          new Date(currentDayISO).getTime() &&
          isSameUser);

      const row = [
        {
          value: emp.name,
          type:
            userType == "organization" || userRole === "userAdmin"
              ? TABLE_CELL_TYPES.LINK
              : TABLE_CELL_TYPES.TEXT,
          to: `/employee-attendance/${emp.id}`,
          keepOnMobile: true,
        },
        {
          type: TABLE_CELL_TYPES.ELEMENT,
          value: (
            <PortalDropdown
              disabled={!canEditAttendance}
              options={statusOptions}
              setSelected={(value) => handleStatusChange(emp.id, value)}
              selected={emp?.status}
            />
          ),
          keepOnMobile: true,
        },
        {
          value: (
            <CustomInput
              disabled={!canEditAttendance}
              type="time"
              value={
                emp.logs[dateKey]?.[emp.logs[dateKey]?.length - 1]?.start || ""
              }
              onChange={(e) => onLogChange(emp.id, "start", e.target.value)}
            />
          ),
          type: TABLE_CELL_TYPES.ELEMENT,
          keepOnMobile: true,
        },
        {
          value: (
            <CustomInput
              disabled={!canEditAttendance}
              type="time"
              value={
                emp.logs[dateKey]?.[emp.logs[dateKey]?.length - 1]?.end || ""
              }
              onChange={(e) => onLogChange(emp.id, "end", e.target.value)}
            />
          ),
          type: TABLE_CELL_TYPES.ELEMENT,
          keepOnMobile: true,
        },
        {
          value: totalHours,
          type: TABLE_CELL_TYPES.TEXT,
          keepOnMobile: true,
        },
      ];

      if (userType === "orgUser") {
        if (isSameUser) {
          row.push(
            {
              value:
                typeof emp?.hourlyRate === "number" ? (
                  emp.hourlyRate
                ) : (
                  <CustomButton
                    variant={"blank"}
                    title={t("show")}
                    icon={padlockIcon}
                    iconSize={16}
                    onClick={() => {
                      setShowUnlockModal(true);
                      setSelectedEmployee(emp);
                      setEmail(userData.email);
                    }}
                  />
                ),
              type: TABLE_CELL_TYPES.ELEMENT,
              keepOnMobile: true,
            },
            {
              value:
                typeof emp?.hourlyRate === "number" ? (
                  `${toMoney(
                    calculateDailySalary(emp.logs, emp.hourlyRate, dateKey)
                  )} ${t("IQD")} `
                ) : (
                  <div
                    style={{
                      padding: "10px 12px",
                      borderRadius: "4px",
                      backgroundColor: "#dedede",
                      color: "#000",
                    }}
                  >
                    xxxxxx
                  </div>
                ),
              type:
                typeof emp?.hourlyRate === "number"
                  ? TABLE_CELL_TYPES.MONEY
                  : TABLE_CELL_TYPES.ELEMENT,
              keepOnMobile: true,
            }
          );
        } else {
          row.push(
            {
              value: (
                <div
                  style={{
                    padding: "10px 12px",
                    borderRadius: "4px",
                    backgroundColor: "#dedede",
                    color: "#000",
                  }}
                >
                  xxxxxx
                </div>
              ),
              type: TABLE_CELL_TYPES.ELEMENT,
              keepOnMobile: true,
            },
            {
              value: (
                <div
                  style={{
                    padding: "10px 12px",
                    borderRadius: "4px",
                    backgroundColor: "#dedede",
                    color: "#000",
                  }}
                >
                  xxxxxx
                </div>
              ),
              type: TABLE_CELL_TYPES.ELEMENT,
              keepOnMobile: true,
            }
          );
        }
      }

      row.push({
        value: (
          <CustomButton
            onClick={() => onSaveAttendance(emp, dateKey)}
            title={t("save")}
            variant="primary"
            size="small"
            icon={saveIcon}
            iconSize={15}
            disabled={!canEditAttendance}
          />
        ),
        type: TABLE_CELL_TYPES.ELEMENT,
        keepOnMobile: true,
      });

      return row;
    });
  };

  const handleUnlockSalary = async () => {
    setUnlockLoading(true);
    if (!password.trim()) {
      setUnlockSalaryError("passwordInputCannotBeEmpty");
      setUnlockLoading(false);
      return;
    }

    if (!email.trim()) {
      setUnlockSalaryError("emailInputCannotBeEmpty");
      setUnlockLoading(false);
      return;
    }

    try {
      const response = await axiosClient.post(
        "/api/attendance/getEmployeeSalaryData",
        {
          userId: selectedEmployee.id,
          email: email,
          password: password,
        }
      );

      if (response.status === 200) {
        setShowUnlockModal(false);
        setSelectedEmployee(null);
        setEmployees((prev) =>
          prev.map((emp) =>
            emp.id === selectedEmployee.id
              ? { ...emp, hourlyRate: response.data.hourlySalary }
              : emp
          )
        );
      }
    } catch (error) {
      console.error("Error unlocking employee salary:", error);
      setUnlockSalaryError("checkPasswordAndTryAgain");
    } finally {
      setUnlockLoading(false);
    }
  };

  function onCloseUnlockModal() {
    setShowUnlockModal(false);
    setSelectedEmployee(null);
    setUnlockSalaryError(null);
    setPassword("");
  }

  return (
    <>
      {showUnlockModal && (
        <Modal
          title="unlockSalaryModleTitle"
          onClose={onCloseUnlockModal}
          error={t(unlockSalaryError)}
          footer={
            <>
              <section className="edit-user-actions">
                <CustomButton
                  title={t("confirm")}
                  icon={saveIcon}
                  iconSize={18}
                  disabled={unlockLoading}
                  onClick={handleUnlockSalary}
                />
                <CustomButton
                  title={t("cancel")}
                  icon={closeIcon}
                  iconSize={15}
                  variant="blank"
                  onClick={onCloseUnlockModal}
                />
              </section>
            </>
          }
        >
          {unlockSalaryError ? (
            <ErrorBox>{t(unlockSalaryError)}</ErrorBox>
          ) : null}
          {unlockLoading ? <Loader size={LOADER_SIZES.SMALL} /> : null}
          <div className="o-column o-column--gap-size_200">
            <span>{t("email")}: </span>
            <CustomInput
              placeholder={t("email")}
              value={email}
              type="email"
              onChange={(e) => setEmail(e.target.value)}
              errorText={
                unlockSalaryError === "emailInputCannotBeEmpty"
                  ? t(unlockSalaryError)
                  : null
              }
            />
          </div>
          <div className="o-column o-column--gap-size_200">
            <span>{t("password")}: </span>
            <PasswordInput
              placeholder={t("password")}
              value={password}
              type="password"
              onChange={(e) => setPassword(e.target.value)}
              errorText={
                unlockSalaryError === "checkPasswordAndTryAgain"
                  ? t(unlockSalaryError)
                  : null
              }
            />
          </div>
        </Modal>
      )}

      {showAreYouSureModal && (
        <AreYouSureModal
          customMessage={t("theCurrentUpdatesWillBeLost")}
          onClose={() => setShowAreYouSureModal(false)}
          onCancel={() => setShowAreYouSureModal(false)}
          confirmIconType="whiteCheck"
          onConfirm={() => {
            if (paginationType === "next") {
              setPagination((prev) => ({
                ...prev,
                currentPage: prev.currentPage + 1,
              }));
            } else {
              setPagination((prev) => ({
                ...prev,
                currentPage: prev.currentPage - 1,
              }));
            }
            setShowAreYouSureModal(false);
          }}
        />
      )}
      <section className="rounded-table-container">
        <table className="i-table">
          <thead className="table-head">
            <tr>
              {withCheckbox && (
                <th className="bold-text">
                  <CustomCheckbox
                    checked={false}
                    onChange={() => {}}
                    color="primary"
                  />
                </th>
              )}
              {titles?.map((column, index) =>
                index > 0 ? (
                  <th
                    key={index}
                    className={`cell-title ${
                      column?.keepOnMobile ? "" : "mobile-hider"
                    }`}
                  >
                    {t(column?.value)}
                  </th>
                ) : (
                  <th key={index} className="bold-text cell-title">
                    {t(column?.value)}
                  </th>
                )
              )}
            </tr>
          </thead>
          <tbody className="table-body">
            {buildEmployeeRows().map((row, index) => (
              <tr key={index}>{renderTableCell(row)}</tr>
            ))}
          </tbody>
        </table>
      </section>
      {pagination && (
        <section className="pagination">
          <div className="pagination-buttons">
            <CustomButton
              variant="blank"
              title={t("previous")}
              onClick={() => {
                if (changedEmployees.length > 0) {
                  setPaginationType("previous");
                  setShowAreYouSureModal(true);
                  return;
                }
                setPagination((prev) => ({
                  ...prev,
                  currentPage: prev.currentPage - 1,
                }));
              }}
              disabled={pagination.currentPage === 1}
              icon={currentDirection === "ltr" ? previousIcon : nextIcon}
              iconSize={15}
              mobileEffect
            />
            <span className="pagination-location">
              {t("page")}: {pagination.currentPage} - {pagination.totalPages}
            </span>
            <CustomButton
              title={t("next")}
              variant="blank"
              onClick={() => {
                if (changedEmployees.length > 0) {
                  setPaginationType("next");
                  setShowAreYouSureModal(true);
                  return;
                }
                setPagination((prev) => ({
                  ...prev,
                  currentPage: prev.currentPage + 1,
                }));
              }}
              disabled={pagination.currentPage === pagination.totalPages}
              icon={currentDirection === "ltr" ? nextIcon : previousIcon}
              iconSize={15}
              reverseIcon
              mobileEffect
            />
          </div>
          <div className="pagination-buttons">
            <span className="pagination-location">
              {t("totalRecords")}: {pagination.totalRecords}
            </span>
          </div>
        </section>
      )}
    </>
  );
};

export default EmployeeAttendanceTable;
